import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid, Card, CardContent, Paper, Typography, TextField, Button, CircularProgress, FormControl, InputLabel, Select, MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { SelectChangeEvent } from '@mui/material/Select';
import axios from 'axios';
import moment from 'moment-timezone';
import { minYmd, nowOperationalDateString, operationalMoment } from '../utils/operationalDateTime';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import { useDrawer } from '../components/context/DrawerContext';
import { stickyListRegionWidth } from '../components/layouts/StickyListToolbar';
import { useNavigate } from 'react-router-dom';
import Title from '../components/Title';
import { useSelection } from '../components/context/SelectionContext';
import SimpleCustomTable from '../components/table/SimpleCustomTable';
import { SimpleColumn, SimpleRow, RowId } from '../components/table/interface';
import { formatCurrency } from '../utils/format/currency';
import { Office } from '../models/office';
import { Collect } from '../models/collect';
import HeaderResponsive from '../components/layouts/HeaderResponsive';
import { isSuperAdminUser } from '../utils/authScope';
import { useResourcePermissions } from '../hooks/useResourcePermissions';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface SummaryTotals {
  totalPays: number;
  totalLends: number;
  totalExpenses: number;
  totalDiscounts: number;
  totalUtility: number;
  totalRefinance: number;
  valueRefinance: number;
  base: number;
  cash: number;
  expectedCash: number;
  cashShortOver: number;
  countPays: number;
  countLends: number;
  countClients: number;
  countCancel: number;
  totalCancel: number;
  totalOffExpPlus: number;
  totalOffExpMinus: number;
  cloveClient: number;
  valueClientClove: number;
  percentageRoute: number;
}

interface ChartPoint {
  date: string;
  totalPays: number;
  totalLends: number;
  totalExpenses: number;
  cashShortOver: number;
}

interface SuperadminOverviewPayload {
  officeCount: number;
  collectCount: number;
  totalClients: number;
  lendsOpenTotal: number;
  lendsClosedTotal: number;
  routeLendsTodayTotal?: number;
  operationalDate?: string;
  rows: Array<{
    office_id: number;
    office_name: string;
    collect_id: number;
    collect_name: string;
    client_count: number;
    lends_open: number;
    lends_closed: number;
    route_lends_today?: number;
  }>;
}

const MetricCard: React.FC<{ title: string; value: string; subtitle?: string }> = ({ title, value, subtitle }) => (
  <Paper
    elevation={2}
    sx={{
      p: 2,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    }}
  >
    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
      {title}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Paper>
);

const Dashboard: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const navigate = useNavigate();
  const {
    office_id,
    setOffice_id,
    collect_id,
    setCollect_id,
    setUser_id,
  } = useSelection();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [offices, setOffices] = useState<Office[]>([]);
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [selectedCollects, setSelectedCollects] = useState<string[]>(collect_id ? [collect_id] : []);
  const [startDate, setStartDate] = useState<string>(operationalMoment().subtract(0, 'days').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState<string>(nowOperationalDateString());
  const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
  const [summaryTotals, setSummaryTotals] = useState<SummaryTotals | null>(null);
  const [chartData, setChartData] = useState<ChartPoint[]>([]);
  const [collectSummaryRows, setCollectSummaryRows] = useState<any[]>([]);
  const [totalEntries, setTotalEntries] = useState<number>(0);
  const [autoFetched, setAutoFetched] = useState<boolean>(false);
  const [authUser, setAuthUser] = useState<{ is_system?: number; username?: string } | null>(null);
  const [mainTab, setMainTab] = useState(0);
  const [superOverview, setSuperOverview] = useState<SuperadminOverviewPayload | null>(null);
  const [superOverviewLoading, setSuperOverviewLoading] = useState(false);
  const [superOverviewLoaded, setSuperOverviewLoaded] = useState(false);
  const { drawerOpen } = useDrawer(); // ya lo usas en tu componente
  const { canRead: canReadDashboardSummary } = useResourcePermissions('/dashboard');
  const { canRead: canReadResumenDia } = useResourcePermissions('/informes/resumendia');
  const { canRead: canReadExpenseReport } = useResourcePermissions('/informes/reportegastos');

  const isSuperAdmin = isSuperAdminUser(authUser);
  const canReadSummaryDay = canReadDashboardSummary || canReadResumenDia || canReadExpenseReport;

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate]);

  useEffect(() => {
    const fetchInitialData = async () => {
      showLoading();
      try {
        const { data: userResp } = await axios.get('user');
        const me = userResp?.data;
        setAuthUser(me ?? null);
        setUser_id(me?.id);

        const officesUrl = isSuperAdminUser(me) ? 'alloffices' : 'offices';
        const { data: officesBody } = await axios.get(officesUrl);
        const officeList: Office[] = Array.isArray(officesBody?.data) ? officesBody.data : [];
        setOffices(officeList);

        if (!office_id && officeList.length > 0) {
          setOffice_id(String(officeList[0].id));
        }
      } catch (error) {
        console.error(error);
        showAlert('Error al cargar la información inicial.', 'error');
      } finally {
        hideLoading();
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchCollects = async () => {
      if (!office_id) {
        setCollectsOffice([]);
        setCollect_id('');
        return;
      }

      try {
        /** El API filtra por permisos; superadmin recibe todos los cobros de la oficina. */
        const { data } = await axios.get(`collectsoffice/${office_id}`);
        const collectList: Collect[] = Array.isArray(data) ? data : [];
        setCollectsOffice(collectList);

        if (!collect_id && collectList.length > 0) {
          setCollect_id(String(collectList[0].id));
        }
      } catch (error) {
        console.error(error);
        showAlert('Error al cargar los cobros de la oficina seleccionada.', 'error');
        setCollectsOffice([]);
      }
    };

    fetchCollects();
  }, [office_id]);

  useEffect(() => {
    if (collect_id) {
      setSelectedCollects([collect_id]);
    } else {
      setSelectedCollects([]);
    }
  }, [collect_id]);

  useEffect(() => {
    setSummaryTotals(null);
    setCollectSummaryRows([]);
    setChartData([]);
    setTotalEntries(0);
  }, [office_id, collect_id]);

  const aggregateByCollect = useCallback((data: any[]) => {
    return data.map((item) => {
      const totals = (item.data || []).reduce(
        (acc: any, closer: any) => {
          acc.totalPays += Number(closer.total_pays) || 0;
          acc.totalLends += Number(closer.total_lends) || 0;
          acc.totalExpenses += Number(closer.total_expenses) || 0;
          acc.totalDiscounts += Number(closer.total_discounts) || 0;
          acc.totalUtility += Number(closer.total_utility) || 0;
          acc.totalOffExpPlus += Number(closer.total_off_exp_plus) || 0;
          acc.totalOffExpMinus += Number(closer.total_off_exp_minus) || 0;
          acc.cashShortOver += Number(closer.cash_short_over) || 0;
          acc.countClients += Number(closer.count_client) || 0;
          return acc;
        },
        {
          totalPays: 0,
          totalLends: 0,
          totalExpenses: 0,
          totalDiscounts: 0,
          totalUtility: 0,
          totalOffExpPlus: 0,
          totalOffExpMinus: 0,
          cashShortOver: 0,
          countClients: 0,
        }
      );

      return {
        id: item.collect_id,
        collect: item.collect_name,
        totalPays: totals.totalPays,
        totalLends: totals.totalLends,
        totalExpenses: totals.totalExpenses,
        totalDiscounts: totals.totalDiscounts,
        totalUtility: totals.totalUtility,
        cashShortOver: totals.cashShortOver,
        countClients: totals.countClients,
      };
    });
  }, []);

  const handleFetchSummary = useCallback(async () => {
    if (!selectedCollects.length) {
      showAlert('Seleccione al menos un cobro.', 'warning');
      return;
    }

    const collectIds = selectedCollects
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id));

    if (!collectIds.length) {
      showAlert('Seleccione un cobro válido.', 'warning');
      return;
    }

    if (!canReadSummaryDay) {
      setSummaryTotals(null);
      setCollectSummaryRows([]);
      setChartData([]);
      setTotalEntries(0);
      showAlert('No tiene permisos para consultar el resumen del día.', 'warning');
      return;
    }

    try {
      setSummaryLoading(true);
      const payload = {
        start_date: startDate,
        end_date: endDate,
        collect_id: collectIds,
      };

      const response = await axios.post('/inform/summaryday', payload);
      const data = response?.data;
      const summaryByCollect = data?.data || [];
      const closers = summaryByCollect.flatMap((item: any) => item.data || []);

      if (!closers.length) {
        setSummaryTotals(null);
        setCollectSummaryRows([]);
        setChartData([]);
        setTotalEntries(0);
        showAlert('No existen registros para los datos seleccionados.', 'info');
        setAutoFetched(true);
        return;
      }

      const aggregatedTotals = closers.reduce(
        (acc: any, closer: any) => {
          acc.totalPays += Number(closer.total_pays) || 0;
          acc.totalLends += Number(closer.total_lends) || 0;
          acc.totalExpenses += Number(closer.total_expenses) || 0;
          acc.totalDiscounts += Number(closer.total_discounts) || 0;
          acc.totalUtility += Number(closer.total_utility) || 0;
          acc.totalRefinance += Number(closer.total_refinance) || 0;
          acc.valueRefinance += Number(closer.value_refinance) || 0;
          acc.base += Number(closer.base) || 0;
          acc.cash += Number(closer.cash) || 0;
          acc.expectedCash += Number(closer.expected_cash) || 0;
          acc.cashShortOver += Number(closer.cash_short_over) || 0;
          acc.countPays += Number(closer.count_pays) || 0;
          acc.countLends += Number(closer.count_lends) || 0;
          acc.countClients += Number(closer.count_client) || 0;
          acc.countCancel += Number(closer.count_cancel) || 0;
          acc.totalCancel += Number(closer.total_cancel) || 0;
          acc.totalOffExpPlus += Number(closer.total_off_exp_plus) || 0;
          acc.totalOffExpMinus += Number(closer.total_off_exp_minus) || 0;
          acc.cloveClient += Number(closer.clove_client) || 0;
          acc.valueClientClove += Number(closer.value_client_clove) || 0;
          acc.percentageRoute += Number(closer.percentage_route) || 0;
          acc.entries += 1;
          return acc;
        },
        {
          totalPays: 0,
          totalLends: 0,
          totalExpenses: 0,
          totalDiscounts: 0,
          totalUtility: 0,
          totalRefinance: 0,
          valueRefinance: 0,
          base: 0,
          cash: 0,
          expectedCash: 0,
          cashShortOver: 0,
          countPays: 0,
          countLends: 0,
          countClients: 0,
          countCancel: 0,
          totalCancel: 0,
          totalOffExpPlus: 0,
          totalOffExpMinus: 0,
          cloveClient: 0,
          valueClientClove: 0,
          percentageRoute: 0,
          entries: 0,
        }
      );

      const summary: SummaryTotals = {
        totalPays: aggregatedTotals.totalPays,
        totalLends: aggregatedTotals.totalLends,
        totalExpenses: aggregatedTotals.totalExpenses,
        totalDiscounts: aggregatedTotals.totalDiscounts,
        totalUtility: aggregatedTotals.totalUtility,
        totalRefinance: aggregatedTotals.totalRefinance,
        valueRefinance: aggregatedTotals.valueRefinance,
        base: aggregatedTotals.base,
        cash: aggregatedTotals.cash,
        expectedCash: aggregatedTotals.expectedCash,
        cashShortOver: aggregatedTotals.cashShortOver,
        countPays: aggregatedTotals.countPays,
        countLends: aggregatedTotals.countLends,
        countClients: aggregatedTotals.countClients,
        countCancel: aggregatedTotals.countCancel,
        totalCancel: aggregatedTotals.totalCancel,
        totalOffExpPlus: aggregatedTotals.totalOffExpPlus,
        totalOffExpMinus: aggregatedTotals.totalOffExpMinus,
        cloveClient: aggregatedTotals.cloveClient,
        valueClientClove: aggregatedTotals.valueClientClove,
        percentageRoute: aggregatedTotals.entries
          ? aggregatedTotals.percentageRoute / aggregatedTotals.entries
          : 0,
      };

      const chartMap = new Map<string, ChartPoint>();
      closers.forEach((closer: any) => {
        const day = closer.date ? String(closer.date).slice(0, 10) : 'Sin fecha';
        const current = chartMap.get(day) || {
          date: day,
          totalPays: 0,
          totalLends: 0,
          totalExpenses: 0,
          cashShortOver: 0,
        };

        current.totalPays += Number(closer.total_pays) || 0;
        current.totalLends += Number(closer.total_lends) || 0;
        current.totalExpenses += Number(closer.total_expenses) || 0;
        current.cashShortOver += Number(closer.cash_short_over) || 0;

        chartMap.set(day, current);
      });

      const chartPoints = Array.from(chartMap.values()).sort((a, b) => a.date.localeCompare(b.date));

      setSummaryTotals(summary);
      setCollectSummaryRows(aggregateByCollect(summaryByCollect));
      setChartData(chartPoints);
      setTotalEntries(closers.length);
      setAutoFetched(true);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        showAlert('No tiene permisos para consultar el resumen del día.', 'error');
        return;
      }
      console.error(error);
      showAlert('Error al obtener la información del resumen.', 'error');
    } finally {
      setSummaryLoading(false);
    }
  }, [aggregateByCollect, canReadSummaryDay, selectedCollects, startDate, endDate, showAlert]);

  const collectSummaryColumns: SimpleColumn[] = useMemo(() => [
    {
      field: 'collect',
      headerName: 'Cobro',
      minWidth: 100,
      wrapText: true,
    },
    {
      field: 'totalPays',
      headerName: 'Abonos',
      align: 'right',
      minWidth: 100,
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.totalPays || 0))}</Typography>
      ),
    },
    {
      field: 'totalLends',
      headerName: 'Préstamos',
      align: 'right',
      minWidth: 140,
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.totalLends || 0))}</Typography>
      ),
    },
    {
      field: 'totalExpenses',
      headerName: 'Gastos',
      align: 'right',
      minWidth: 140,
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.totalExpenses || 0))}</Typography>
      ),
    },
    {
      field: 'totalDiscounts',
      headerName: 'Descuentos',
      align: 'right',
      minWidth: 140,
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.totalDiscounts || 0))}</Typography>
      ),
    },
    {
      field: 'totalUtility',
      headerName: 'Utilidad',
      align: 'right',
      minWidth: 140,
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.totalUtility || 0))}</Typography>
      ),
    },
    {
      field: 'cashShortOver',
      headerName: 'Cuadre Caja',
      align: 'right',
      minWidth: 140,
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.cashShortOver || 0))}</Typography>
      ),
    },
    {
      field: 'countClients',
      headerName: 'Clientes',
      align: 'center',
      minWidth: 120,
      renderCell: ({ row }) => (
        <Typography variant="body2">{Number(row.countClients || 0).toLocaleString('es-CO')}</Typography>
      ),
    },
  ], []);

  const metrics = useMemo(() => {
    if (!summaryTotals) return [];

    const netResult = summaryTotals.base
      + summaryTotals.totalPays
      - summaryTotals.totalExpenses
      - summaryTotals.totalDiscounts
      - summaryTotals.totalLends;
    const cashVariance = summaryTotals.cash - summaryTotals.expectedCash;

    return [
      { title: 'Total Abonos', value: formatCurrency(summaryTotals.totalPays) },
      { title: 'Total Préstamos', value: formatCurrency(summaryTotals.totalLends) },
      { title: 'Gastos Totales', value: formatCurrency(summaryTotals.totalExpenses) },
      { title: 'Descuentos', value: formatCurrency(summaryTotals.totalDiscounts) },
      { title: 'Utilidad', value: formatCurrency(summaryTotals.totalUtility) },
      {
        title: 'Refinanciaciones',
        value: formatCurrency(summaryTotals.totalRefinance),
        subtitle: `Monto refinanciado: ${formatCurrency(summaryTotals.valueRefinance)}`,
      },
      { title: 'Clientes Atendidos', value: summaryTotals.countClients.toLocaleString('es-CO') },
      { title: 'Abonos Registrados', value: summaryTotals.countPays.toLocaleString('es-CO') },
      {
        title: 'Efectivo Reportado',
        value: formatCurrency(summaryTotals.cash),
        subtitle: `Esperado: ${formatCurrency(summaryTotals.expectedCash)}`,
      },
      {
        title: 'Cuadre de Caja',
        value: formatCurrency(summaryTotals.cashShortOver),
        subtitle: cashVariance === 0 ? 'Sin diferencia' : `Desfase: ${formatCurrency(cashVariance)}`,
      },
      { title: 'Resultado Neto', value: formatCurrency(netResult) },
      {
        title: 'Cobertura Ruta',
        value: `${summaryTotals.percentageRoute.toFixed(1)}%`,
        subtitle: 'Promedio de cierres',
      },
    ];
  }, [summaryTotals]);

  const handleOfficeSelect = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setOffice_id(value);
    setCollect_id('');
    setSelectedCollects([]);
    setSummaryTotals(null);
    setCollectSummaryRows([]);
    setChartData([]);
    setTotalEntries(0);
    setAutoFetched(false);
  };

  const handleCollectSelect = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setCollect_id(value);
    if (value) {
      setSelectedCollects([value]);
    } else {
      setSelectedCollects([]);
    }
    setSummaryTotals(null);
    setCollectSummaryRows([]);
    setChartData([]);
    setTotalEntries(0);
    setAutoFetched(false);
  };

  useEffect(() => {
    if (!autoFetched && selectedCollects.length > 0 && !summaryLoading) {
      handleFetchSummary();
    }
  }, [autoFetched, selectedCollects, summaryLoading, handleFetchSummary]);

  const loadSuperadminOverview = useCallback(async () => {
    setSuperOverviewLoading(true);
    try {
      const { data } = await axios.get<{ data: SuperadminOverviewPayload }>('dashboard/global-org-overview', {
        params: { date: nowOperationalDateString() },
      });
      setSuperOverview(data?.data ?? null);
    } catch (e: unknown) {
      let suffix = '';
      if (axios.isAxiosError(e)) {
        const st = e.response?.status;
        const body = e.response?.data as { message?: string } | undefined;
        const apiMsg = body?.message && String(body.message).trim();
        if (apiMsg) {
          suffix = ` ${apiMsg}`;
        } else if (st === 404) {
          suffix = ' El servidor no expone esta ruta (¿API desactualizada?).';
        } else if (st === 401 || st === 403) {
          suffix = ' Sin sesión o permisos.';
        } else if (st) {
          suffix = ` (HTTP ${st})`;
        } else if (e.code === 'ERR_NETWORK') {
          suffix = ' No hubo respuesta (red, CORS o API caída).';
        }
        console.error('loadSuperadminOverview', e.response?.data ?? e.message);
      }
      showAlert(`No se pudo cargar la vista global.${suffix}`.trim(), 'error');
      setSuperOverview(null);
    } finally {
      setSuperOverviewLoaded(true);
      setSuperOverviewLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    if (mainTab === 1 && !superOverviewLoaded && !superOverviewLoading) {
      loadSuperadminOverview();
    }
  }, [mainTab, superOverviewLoaded, superOverviewLoading, loadSuperadminOverview]);

  const officeAggregateRows: SimpleRow[] = useMemo(() => {
    if (!superOverview?.rows?.length) return [];
    const map = new Map<
      number,
      {
        office_id: number;
        office_name: string;
        collectCount: number;
        clientCount: number;
        lendsOpen: number;
        lendsClosed: number;
        routeLendsToday: number;
      }
    >();
    for (const r of superOverview.rows) {
      const prev = map.get(r.office_id) ?? {
        office_id: r.office_id,
        office_name: r.office_name,
        collectCount: 0,
        clientCount: 0,
        lendsOpen: 0,
        lendsClosed: 0,
        routeLendsToday: 0,
      };
      prev.collectCount += 1;
      prev.clientCount += r.client_count;
      prev.lendsOpen += Number(r.lends_open ?? 0);
      prev.lendsClosed += Number(r.lends_closed ?? 0);
      prev.routeLendsToday += Number(r.route_lends_today ?? 0);
      map.set(r.office_id, prev);
    }
    return Array.from(map.values())
      .sort((a, b) => a.office_name.localeCompare(b.office_name, 'es'))
      .map((o) => ({
        id: `office-${o.office_id}` as RowId,
        office_name: o.office_name,
        collectCount: o.collectCount,
        clientCount: o.clientCount,
        lendsOpen: o.lendsOpen,
        lendsClosed: o.lendsClosed,
        routeLendsToday: o.routeLendsToday,
      }));
  }, [superOverview]);

  const superDetailRows: SimpleRow[] = useMemo(() => {
    if (!superOverview?.rows?.length) return [];
    return superOverview.rows.map((r) => ({
      id: r.collect_id as RowId,
      office_name: r.office_name,
      collect_name: r.collect_name,
      client_count: r.client_count,
      lends_open: r.lends_open ?? 0,
      lends_closed: r.lends_closed ?? 0,
      route_lends_today: r.route_lends_today ?? 0,
    }));
  }, [superOverview]);

  const officeAggregateColumns: SimpleColumn[] = useMemo(
    () => [
      {
        field: 'office_name',
        headerName: 'Oficina',
        minWidth: 180,
        wrapText: true,
      },
      {
        field: 'collectCount',
        headerName: 'Cobros',
        align: 'center',
        minWidth: 100,
        renderCell: ({ row }) => (
          <Typography variant="body2">{Number(row.collectCount ?? 0).toLocaleString('es-CO')}</Typography>
        ),
      },
      {
        field: 'clientCount',
        headerName: 'Clientes',
        align: 'center',
        minWidth: 100,
        renderCell: ({ row }) => (
          <Typography variant="body2">{Number(row.clientCount ?? 0).toLocaleString('es-CO')}</Typography>
        ),
      },
      {
        field: 'lendsOpen',
        headerName: 'Prést. abiertos',
        align: 'center',
        minWidth: 120,
        renderCell: ({ row }) => (
          <Typography variant="body2">{Number(row.lendsOpen ?? 0).toLocaleString('es-CO')}</Typography>
        ),
      },
      {
        field: 'lendsClosed',
        headerName: 'Prést. cerrados',
        align: 'center',
        minWidth: 120,
        renderCell: ({ row }) => (
          <Typography variant="body2">{Number(row.lendsClosed ?? 0).toLocaleString('es-CO')}</Typography>
        ),
      },
      {
        field: 'routeLendsToday',
        headerName: 'En ruta (hoy)',
        align: 'center',
        minWidth: 130,
        renderCell: ({ row }) => (
          <Typography variant="body2">{Number(row.routeLendsToday ?? 0).toLocaleString('es-CO')}</Typography>
        ),
      },
    ],
    [],
  );

  const superDetailColumns: SimpleColumn[] = useMemo(
    () => [
      {
        field: 'office_name',
        headerName: 'Oficina',
        minWidth: 160,
        wrapText: true,
      },
      {
        field: 'collect_name',
        headerName: 'Cobro',
        minWidth: 160,
        wrapText: true,
      },
      {
        field: 'client_count',
        headerName: 'Clientes',
        align: 'center',
        minWidth: 110,
        renderCell: ({ row }) => (
          <Typography variant="body2">{Number(row.client_count ?? 0).toLocaleString('es-CO')}</Typography>
        ),
      },
      {
        field: 'lends_open',
        headerName: 'Prést. abiertos',
        align: 'center',
        minWidth: 120,
        renderCell: ({ row }) => (
          <Typography variant="body2">{Number(row.lends_open ?? 0).toLocaleString('es-CO')}</Typography>
        ),
      },
      {
        field: 'lends_closed',
        headerName: 'Prést. cerrados',
        align: 'center',
        minWidth: 120,
        renderCell: ({ row }) => (
          <Typography variant="body2">{Number(row.lends_closed ?? 0).toLocaleString('es-CO')}</Typography>
        ),
      },
      {
        field: 'route_lends_today',
        headerName: 'En ruta (hoy)',
        align: 'center',
        minWidth: 130,
        renderCell: ({ row }) => (
          <Typography variant="body2">{Number(row.route_lends_today ?? 0).toLocaleString('es-CO')}</Typography>
        ),
      },
    ],
    [],
  );

  return (
    <HeaderResponsive>
      <Box sx={{ width: '100%', maxWidth: '100%', px: isMobile ? 1 : 3 }}>
        <Box sx={{ mb: 1 }}>
          <Title>Panel principal</Title>
          <Tabs
            value={mainTab}
            onChange={(_, v: number) => setMainTab(v)}
            sx={{ mt: 1 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Resumen diario" />
            <Tab label="Vista global" />
          </Tabs>
        </Box>
      </Box>
      <Card sx={{ minHeight: 600, height: mainTab === 1 ? 'auto' : 600, overflowY: 'scroll' }}>
        <CardContent
          sx={{
            width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto',
          }}
        >
          {mainTab === 1 ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
                {isSuperAdmin
                  ? 'Totales de todas las oficinas: cobros, clientes y préstamos (abierto = saldo activo, cerrado = liquidado). «En ruta (hoy)» cuenta préstamos asignados a la ruta del día operativo (zona Bogotá).'
                  : 'Totales de las oficinas y cobros a los que tiene acceso: clientes y préstamos (abierto = saldo activo, cerrado = liquidado). «En ruta (hoy)» cuenta préstamos asignados a la ruta del día operativo (zona Bogotá).'}
              </Typography>
              {superOverviewLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CircularProgress size={22} />
                  <Typography variant="body2">Cargando…</Typography>
                </Box>
              )}
              {superOverviewLoaded && !superOverviewLoading && !superOverview && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No hay datos para mostrar o no se pudo obtener el resumen.
                </Typography>
              )}
              {superOverview && !superOverviewLoading && (
                <>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <MetricCard
                        title="Oficinas"
                        value={superOverview.officeCount.toLocaleString('es-CO')}
                        subtitle="Con al menos un cobro configurado"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MetricCard
                        title="Cobros"
                        value={superOverview.collectCount.toLocaleString('es-CO')}
                        subtitle={isSuperAdmin ? 'Total en el sistema' : 'En sus cobros asignados'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MetricCard
                        title="Clientes"
                        value={superOverview.totalClients.toLocaleString('es-CO')}
                        subtitle={isSuperAdmin ? 'Suma por todos los cobros' : 'Suma en sus cobros'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <MetricCard
                        title="Préstamos abiertos"
                        value={(superOverview.lendsOpenTotal ?? 0).toLocaleString('es-CO')}
                        subtitle="Activos (aún con saldo)"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <MetricCard
                        title="Préstamos cerrados"
                        value={(superOverview.lendsClosedTotal ?? 0).toLocaleString('es-CO')}
                        subtitle="Liquidados"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <MetricCard
                        title="Préstamos en ruta (hoy)"
                        value={(superOverview.routeLendsTodayTotal ?? 0).toLocaleString('es-CO')}
                        subtitle={
                          superOverview.operationalDate
                            ? `Rutas con fecha ${superOverview.operationalDate}`
                            : 'Día operativo (Bogotá)'
                        }
                      />
                    </Grid>
                  </Grid>
                  <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Por oficina
                    </Typography>
                    <SimpleCustomTable
                      rows={officeAggregateRows}
                      columns={officeAggregateColumns}
                      maxHeight={360}
                      showBorders
                      wrapCellContent
                    />
                  </Paper>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Clientes por cobro
                    </Typography>
                    {superOverview.operationalDate ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Columna «En ruta (hoy)»: préstamos en la ruta con fecha {superOverview.operationalDate}.
                      </Typography>
                    ) : null}
                    <SimpleCustomTable
                      rows={superDetailRows}
                      columns={superDetailColumns}
                      maxHeight={480}
                      showBorders
                      showCount
                      wrapCellContent
                    />
                  </Paper>
                </>
              )}
            </Box>
          ) : (
            <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} md={4} lg={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="dashboard-office-label">Oficina</InputLabel>
                  <Select
                    labelId="dashboard-office-label"
                    id="dashboard-office-select"
                    value={office_id}
                    label="Oficina"
                    onChange={handleOfficeSelect}
                  >
                    {offices.map((office) => (
                      <MenuItem key={office.id} value={String(office.id)}>
                        {office.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4} lg={3}>
                <FormControl fullWidth size="small" disabled={!collectsOffice.length}>
                  <InputLabel id="dashboard-collect-label">Cobro</InputLabel>
                  <Select
                    labelId="dashboard-collect-label"
                    id="dashboard-collect-select"
                    value={collect_id}
                    label="Cobro"
                    onChange={handleCollectSelect}
                  >
                    {collectsOffice.map((collect) => (
                      <MenuItem key={collect.id} value={String(collect.id)}>
                        {collect.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4} lg={3}>
                <TextField
                  fullWidth
                  label="Desde"
                  type="date"
                  size="small"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: minYmd(endDate, nowOperationalDateString()) }}
                />
              </Grid>

              <Grid item xs={12} md={4} lg={3}>
                <TextField
                  fullWidth
                  label="Hasta"
                  type="date"
                  size="small"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: startDate, max: nowOperationalDateString() }}
                />
              </Grid>

              <Grid item xs={12} md={4} lg={'auto'}>
                <Button
                  variant="contained"
                  onClick={handleFetchSummary}
                  disabled={summaryLoading}
                >
                  Consultar
                </Button>
              </Grid>

              {summaryLoading && (
                <Grid item xs={12} lg={'auto'}>
                  <CircularProgress size={24} />
                </Grid>
              )}
            </Grid>
          </Paper>

          {summaryTotals ? (
            <Grid container spacing={2} mb={summaryTotals ? 3 : 0}>
              {metrics.map((metric) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={metric.title}>
                  <MetricCard title={metric.title} value={metric.value} subtitle={metric.subtitle} />
                </Grid>
              ))}
            </Grid>
          ) : (
            !summaryLoading && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Seleccione una oficina y un cobro, ajuste el rango de fechas y presione "Consultar" para ver el resumen diario.
              </Typography>
            )
          )}

          {chartData.length > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Evolución de Abonos, Préstamos y Gastos
              </Typography>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(value) => moment(value).format('DD/MM')} />
                  <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`} />
                  <RechartsTooltip
                    formatter={(value: number) => formatCurrency(Number(value))}
                    labelFormatter={(label) => moment(label).format('DD MMM YYYY')}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="totalPays" name="Abonos" stroke={theme.palette.primary.main} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="totalLends" name="Préstamos" stroke={theme.palette.success.main} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="totalExpenses" name="Gastos" stroke={theme.palette.error.main} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
              <Typography variant="caption" color="text.secondary">
                Registros analizados: {totalEntries}
              </Typography>
            </Paper>
          )}

          {collectSummaryRows.length > 0 && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Resumen por Cobro
              </Typography>
              <SimpleCustomTable
                rows={collectSummaryRows}
                columns={collectSummaryColumns}
                maxHeight={400}
                showBorders
                wrapCellContent
              />
            </Paper>
          )}
            </>
          )}
        </CardContent>
      </Card>
    </HeaderResponsive >

  );
};

export default Dashboard;
